import type { AddressForm } from 'features/conversation-preferences/services/conversation-preferences-service';

/** User-typed Ask keeps length only — no language instruction; the text is theirs. */
export const ASK_SHORT_REPLY = 'Reply in 2 lines at most. Use 20 words at most.';

export type BuddyPagePrompt = {
  path: string;
  useAi: boolean;
  /** English, model-facing name of the page. Never shown. */
  topic: string;
  /** UILM key of the written line; base key Sie, `_informal` du. */
  lineKey: string;
  /** The English line, used when no translation is loaded. */
  defaultValue: string;
};

/**
 * What Buddy explains on each page. The written line is shown when `useAi` is false or the call
 * fails, and is the key point the AI paraphrases otherwise.
 */
const ROUTES: BuddyPagePrompt[] = [
  {
    path: '/chat-history',
    useAi: true,
    topic: 'chat history',
    lineKey: 'BUDDY_PAGE_CHAT_HISTORY',
    defaultValue: 'All your chats live here. Pick up any of them right where you left off.',
  },
  {
    path: '/chat',
    useAi: true,
    topic: 'chat',
    lineKey: 'BUDDY_PAGE_CHAT',
    defaultValue: "Tell me what you need, and I'll build the right agent for it.",
  },
  {
    path: '/project',
    useAi: true,
    topic: 'projects',
    lineKey: 'BUDDY_PAGE_PROJECTS',
    defaultValue: 'A project brings your chats, files and agents together in one place.',
  },
  {
    path: '/custom-gpts',
    useAi: true,
    topic: 'custom agents',
    lineKey: 'BUDDY_PAGE_CUSTOM_AGENTS',
    defaultValue: 'Open an agent and put it straight to work.',
  },
  {
    path: '/agent-details',
    useAi: true,
    topic: 'agent details',
    lineKey: 'BUDDY_PAGE_AGENT_DETAILS',
    defaultValue: 'Here you decide what an agent does: its job, its instructions and its tools.',
  },
  {
    path: '/agents',
    useAi: true,
    topic: 'agents',
    lineKey: 'BUDDY_PAGE_AGENTS',
    defaultValue: 'All your agents are here, ready whenever you are.',
  },
  {
    path: '/tool-details',
    useAi: true,
    topic: 'tool details',
    lineKey: 'BUDDY_PAGE_TOOL_DETAILS',
    defaultValue: 'Set up a tool here so your agents can use it.',
  },
  {
    path: '/tools',
    useAi: true,
    topic: 'tools',
    lineKey: 'BUDDY_PAGE_TOOLS',
    defaultValue: 'Tools let your agents get real work done.',
  },
  {
    path: '/skills',
    useAi: true,
    topic: 'skills',
    lineKey: 'BUDDY_PAGE_SKILLS',
    defaultValue: 'Skills teach your agents to do specific tasks really well.',
  },
  {
    path: '/prompts',
    useAi: true,
    topic: 'prompts',
    lineKey: 'BUDDY_PAGE_PROMPTS',
    defaultValue: 'Save a prompt once and reuse it as often as you like.',
  },
  {
    path: '/workflows',
    useAi: true,
    topic: 'workflows',
    lineKey: 'BUDDY_PAGE_WORKFLOWS',
    defaultValue: "Workflows chain the steps together, so you don't have to.",
  },
  {
    path: '/knowledge-base',
    useAi: true,
    topic: 'knowledge base',
    lineKey: 'BUDDY_PAGE_KNOWLEDGE_BASE',
    defaultValue: 'Your agents use whatever you add here in their answers.',
  },
  {
    path: '/media-library',
    useAi: true,
    topic: 'media library',
    lineKey: 'BUDDY_PAGE_MEDIA_LIBRARY',
    defaultValue: 'Your files are here, within reach for you and your agents.',
  },
  {
    path: '/integrations',
    useAi: true,
    topic: 'integrations',
    lineKey: 'BUDDY_PAGE_INTEGRATIONS',
    defaultValue: 'Connect the apps you already use, and your agents can work with them.',
  },
  {
    path: '/scheduler',
    useAi: true,
    topic: 'scheduler',
    lineKey: 'BUDDY_PAGE_SCHEDULER',
    defaultValue: 'Tell an agent when to run, and it starts right on time.',
  },
  {
    path: '/configure',
    useAi: true,
    topic: 'settings',
    lineKey: 'BUDDY_PAGE_SETTINGS',
    defaultValue: 'Set up Klax the way your team works.',
  },
  {
    path: '/usage',
    useAi: true,
    topic: 'usage',
    lineKey: 'BUDDY_PAGE_USAGE',
    defaultValue: 'See how much your agents have been working.',
  },
  {
    path: '/profile',
    useAi: true,
    topic: 'profile',
    lineKey: 'BUDDY_PAGE_PROFILE',
    defaultValue: 'This is you: your name, your language and how you sign in.',
  },
  {
    path: '/identity-management',
    useAi: true,
    topic: 'identity management',
    lineKey: 'BUDDY_PAGE_PEOPLE',
    defaultValue: 'Invite colleagues and decide who can do what.',
  },
  {
    path: '/organization-management',
    useAi: true,
    topic: 'organization',
    lineKey: 'BUDDY_PAGE_ORGANIZATION',
    defaultValue: "Manage your organization's plan and members here.",
  },
  {
    path: '/subscription-plans',
    useAi: true,
    topic: 'plans',
    lineKey: 'BUDDY_PAGE_PLANS',
    defaultValue: 'Just pick the plan that suits you.',
  },
  {
    path: '/checkout',
    useAi: true,
    topic: 'plans',
    lineKey: 'BUDDY_PAGE_PLANS',
    defaultValue: 'Just pick the plan that suits you.',
  },
  {
    path: '/payment',
    useAi: true,
    topic: 'plans',
    lineKey: 'BUDDY_PAGE_PLANS',
    defaultValue: 'Just pick the plan that suits you.',
  },
  {
    path: '/comingsoon',
    useAi: true,
    topic: 'a part that is coming soon',
    lineKey: 'BUDDY_PAGE_COMING_SOON',
    defaultValue: 'This part is coming soon. Check back shortly.',
  },
];

export const ROUTE_PROMPTS = ROUTES;

export const DEFAULT_PROMPT: BuddyPagePrompt = {
  path: '',
  useAi: true,
  topic: 'the app',
  lineKey: 'BUDDY_PAGE_DEFAULT',
  defaultValue: 'Klax is where you build agents that work for you.',
};

export type BuddyIconPrompt = Omit<BuddyPagePrompt, 'path'> & { id: string; match: string[] };

/** Header icon lines. `match` is matched against the button label. */
export const ICON_PROMPTS: BuddyIconPrompt[] = [
  {
    id: 'invite',
    match: ['invite', 'participant'],
    useAi: true,
    topic: 'group chat',
    lineKey: 'BUDDY_ICON_INVITE',
    defaultValue: 'Bring others into this chat and plan together.',
  },
  {
    id: 'incognito',
    match: ['incognito'],
    useAi: true,
    topic: 'incognito mode',
    lineKey: 'BUDDY_ICON_INCOGNITO',
    defaultValue: "In incognito mode, this chat stays out of your history and I won't remember it.",
  },
  {
    id: 'notifications',
    match: ['notification'],
    useAi: true,
    topic: 'notifications',
    lineKey: 'BUDDY_ICON_NOTIFICATIONS',
    defaultValue: "This is where you see what's waiting for you.",
  },
];

export function promptForIcon(label: string) {
  const text = String(label || '').toLowerCase();
  return ICON_PROMPTS.find((item) => item.match.some((key) => text.includes(key))) || null;
}

/** How each address form is said where the language has one; English has a single form. */
const ADDRESS_WORDS = [
  { language: 'de', informal: 'du', formal: 'Sie' },
  { language: 'fr', informal: 'tu', formal: 'vous' },
  { language: 'it', informal: 'tu', formal: 'Lei' },
] as const;

/** The reply language and form for the UI language. Formal when the form is unknown. */
export function buddyLanguageRule(
  language = 'en-US',
  addressForm?: AddressForm | 'unknown' | null
) {
  const code = language.toLowerCase();
  const base = code.split('-')[0];
  const words = ADDRESS_WORDS.find((item) => item.language === base);
  if (!words) return 'Reply in English.';
  const form = `Address the user with "${words[addressForm === 'informal' ? 'informal' : 'formal']}".`;
  if (code === 'de-ch') return `Reply in Swiss Standard German: always write "ss", never "ß". ${form}`;
  if (base === 'de') return `Reply in German (Germany). ${form}`;
  return `Reply in ${base === 'fr' ? 'French' : 'Italian'}. ${form}`;
}

/** One English instruction: the page, its localized key point, then the language and form. */
export function buildBuddyPagePrompt({
  topic,
  line,
  language,
  addressForm,
}: {
  topic: string;
  line: string;
  language?: string;
  addressForm?: AddressForm | 'unknown' | null;
}) {
  return `Explain the "${topic}" part of the Klax app to the user in your own words. The key point: ${line}
${buddyLanguageRule(language, addressForm)} Reply in 2 lines at most. Use 20 words at most. Keep the names Buddy and Klax as they are.`;
}

/** Swiss Standard German never writes "ß". */
export const regionalSpelling = (text: string, language?: string) =>
  language?.toLowerCase() === 'de-ch' ? text.replaceAll('ß', 'ss') : text;
